import {
  AiOfferBatch,
  AiOfferBatchDetailResponse,
  AiOfferBatchPendingCountResponse,
  AiOfferBatchRequest,
  AiOfferBatchesResponse,
  AiOfferDraftItem,
  AiOfferDraftItemUpdateRequest,
  BulkInvestmentProposalResponse,
  Id,
  PropallyAgentV1Api,
} from "./generated/src";

// サーバー側の上限（ConcernAgent::AiOfferBatchConcern::MAX_TARGET_USER_COUNT）と揃えること。
export const AI_OFFER_MAX_TARGET_COUNT = 50;

// サーバー側の生成上限（Gemini::OfferMessageService）と揃えること。
export const SHORT_APPEAL_MESSAGE_MAX_LENGTH = 70;
export const PROPOSAL_CONTENT_MAX_LENGTH = 1000;

const promisify = <T,>(call: (callback: (error: object, data: T, response: object) => void) => void): Promise<T> =>
  new Promise((resolve, reject) => {
    call((error: object, data: T) => {
      if (error) {
        console.error(error);
        reject(error);
      } else {
        resolve(data);
      }
    });
  });

export const createAiOfferBatch = async (userIds: number[], isReProposal: boolean): Promise<Id> => {
  const request = { user_ids: userIds, re_proposal: isReProposal } as AiOfferBatchRequest;
  const api = new PropallyAgentV1Api();
  return promisify<Id>((cb) => api.agentV1AiOfferBatchesPost(request, cb));
};

export const fetchAiOfferBatches = async (): Promise<AiOfferBatch[]> => {
  const api = new PropallyAgentV1Api();
  const response = await promisify<AiOfferBatchesResponse>((cb) => api.agentV1AiOfferBatchesGet(cb));
  return response.batches ?? [];
};

export const fetchAiOfferBatch = async (batchId: number): Promise<AiOfferBatchDetailResponse> => {
  const api = new PropallyAgentV1Api();
  return promisify<AiOfferBatchDetailResponse>((cb) => api.agentV1AiOfferBatchesIdGet(batchId, cb));
};

export const fetchAiOfferPendingCount = async (): Promise<number> => {
  const api = new PropallyAgentV1Api();
  const response = await promisify<AiOfferBatchPendingCountResponse>((cb) =>
    api.agentV1AiOfferBatchesPendingCountGet(cb)
  );
  return response.pending_count ?? 0;
};

export interface AiOfferDraftItemUpdate {
  shortAppealMessage?: string;
  proposalContent?: string;
  confirmed?: boolean;
}

export const updateAiOfferDraftItem = async (
  batchId: number,
  itemId: number,
  update: AiOfferDraftItemUpdate
): Promise<AiOfferDraftItem> => {
  // 指定したキーだけを送る（省略したキーはサーバー側でも触らない）。
  const request = {} as AiOfferDraftItemUpdateRequest;
  if (update.shortAppealMessage !== undefined) request.short_appeal_message = update.shortAppealMessage;
  if (update.proposalContent !== undefined) request.proposal_content = update.proposalContent;
  if (update.confirmed !== undefined) request.confirmed = update.confirmed;

  const api = new PropallyAgentV1Api();
  return promisify<AiOfferDraftItem>((cb) => api.agentV1AiOfferBatchesIdItemsItemIdPatch(batchId, itemId, request, cb));
};

export const regenerateAiOfferDraftItem = async (batchId: number, itemId: number): Promise<AiOfferDraftItem> => {
  const api = new PropallyAgentV1Api();
  return promisify<AiOfferDraftItem>((cb) =>
    api.agentV1AiOfferBatchesIdItemsItemIdRegenerationsPost(batchId, itemId, cb)
  );
};

export const confirmAllAiOfferDraftItems = async (batchId: number): Promise<AiOfferBatchDetailResponse> => {
  const api = new PropallyAgentV1Api();
  return promisify<AiOfferBatchDetailResponse>((cb) => api.agentV1AiOfferBatchesIdConfirmationsPost(batchId, cb));
};

export const deliverAiOfferBatch = async (batchId: number): Promise<BulkInvestmentProposalResponse> => {
  const api = new PropallyAgentV1Api();
  return promisify<BulkInvestmentProposalResponse>((cb) => api.agentV1AiOfferBatchesIdDeliveriesPost(batchId, cb));
};

export const cancelAiOfferBatch = async (batchId: number): Promise<AiOfferBatch> => {
  const api = new PropallyAgentV1Api();
  return promisify<AiOfferBatch>((cb) => api.agentV1AiOfferBatchesIdCancellationsPost(batchId, cb));
};

export const isUnprocessedAiOfferBatch = (batch: AiOfferBatch): boolean =>
  batch.status === "generating" || batch.status === "ready";

// api_schema の AiOfferBatch.status の enum と一致させること。
const BATCH_STATUS_LABELS: Record<string, string> = {
  generating: "生成中",
  ready: "確認待ち",
  sent: "送信済み",
  canceled: "破棄",
};

export const aiOfferBatchStatusLabel = (status?: string): string =>
  BATCH_STATUS_LABELS[status ?? ""] ?? "-";
